import { ERROR_MESSAGES } from '@/lib/configs/auth-config'
import { turnstileHeaders } from '@/lib/turnstile'
import { getOrCreateDeviceId } from '@/lib/utils/device-id'
import { orderXClient } from '@/service/client'
import { PublicAccountList } from '@/types/generated/common/account'
import {
  GoogleLoginRequest,
  GoogleLoginResponse,
  LoginRequest,
  LoginResponse,
  SignupRequest,
  SignupResponse,
} from '@/types/generated/common/auth'
import {
  ConfirmPasswordResetRequest,
  ConfirmPasswordResetResponse,
  RequestPasswordResetRequest,
  RequestPasswordResetResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  ServerMessage,
  VerifyEmailRequest,
  VerifyEmailResponse,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

export async function loginWithEmail(payload: LoginRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      login: { ...payload, deviceId: getOrCreateDeviceId() },
    })
    const loginResponse = data.loginResponse as LoginResponse
    if (!loginResponse.success) {
      if (loginResponse.errorMessage === ERROR_MESSAGES.PASSKEY_AUTH_USER) {
        return loginResponse
      }
      return Promise.reject({
        status: 400,
        message: loginResponse.errorMessage,
      })
    }
    return loginResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function signupWithEmail(payload: SignupRequest, turnstileToken?: string) {
  try {
    const { data } = await orderXClient.post<ServerMessage>(
      '/api',
      { signup: payload },
      turnstileHeaders(turnstileToken)
    )
    const signupResponse = data.signupResponse as SignupResponse
    if (!signupResponse.success) {
      return Promise.reject({
        status: 400,
        message: signupResponse.errorMessage,
      })
    }
    return signupResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function fetchUserAccounts() {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api')
    const userAccountsResponse = data.userAccountsResponse as PublicAccountList
    return userAccountsResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function verifyEmail(payload: VerifyEmailRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { verifyEmail: payload })
    const verifyEmailResponse = data.verifyEmailResponse as VerifyEmailResponse
    if (!verifyEmailResponse.success) {
      return Promise.reject({
        status: 400,
        message: verifyEmailResponse.message,
      })
    }
    return verifyEmailResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function resendVerificationEmail(
  payload: ResendVerificationRequest,
  turnstileToken?: string
) {
  try {
    const { data } = await orderXClient.post<ServerMessage>(
      '/api',
      { resendVerification: payload },
      turnstileHeaders(turnstileToken)
    )
    const resendVerificationResponse = data.resendVerificationResponse as ResendVerificationResponse
    if (!resendVerificationResponse.success) {
      return Promise.reject({
        status: 400,
        message: resendVerificationResponse.message,
      })
    }
    return resendVerificationResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function googleLogin(payload: GoogleLoginRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      googleLogin: { ...payload, deviceId: getOrCreateDeviceId() },
    })
    const googleLoginResponse = data.googleLoginResponse as GoogleLoginResponse
    if (!googleLoginResponse.success) {
      return Promise.reject({
        status: 400,
        message: googleLoginResponse.errorMessage,
      })
    }
    return googleLoginResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function requestPasswordReset(
  payload: RequestPasswordResetRequest,
  turnstileToken?: string
) {
  try {
    const { data } = await orderXClient.post<ServerMessage>(
      '/api',
      { requestPasswordReset: payload },
      turnstileHeaders(turnstileToken)
    )
    const requestPasswordResetResponse =
      data.requestPasswordResetResponse as RequestPasswordResetResponse
    if (!requestPasswordResetResponse.success) {
      return Promise.reject({
        status: 400,
        message: requestPasswordResetResponse.message,
      })
    }
    return requestPasswordResetResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function confirmPasswordReset(payload: ConfirmPasswordResetRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      confirmPasswordReset: payload,
    })
    const confirmPasswordResetResponse =
      data.confirmPasswordResetResponse as ConfirmPasswordResetResponse
    if (!confirmPasswordResetResponse.success) {
      return Promise.reject({
        status: 400,
        message: confirmPasswordResetResponse.message,
      })
    }
    return confirmPasswordResetResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}
